import { useCallback } from "react";

import { Flex } from "@chakra-ui/react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router";

import { routes } from "config/routes";
import { ScanPlaceholder } from "flows/Inventory/shared/components/ScanPlaceholder";
import { InventoryProduct } from "flows/Inventory/shared/models/inventoryProduct/types";
import { Page } from "shared/components/Page";
import { SpinnerModal } from "shared/components/SpinnerModal";
import { useBarcodeScanner } from "shared/hooks/useBarcodeScanner";
import { useScan } from "shared/hooks/useScan/useScan";
import { NavigationHeader } from "ui/NavigationHeader/NavigationHeader";
import { isShelfNumberFormat } from "utils/scan";

import { CorrectionsTextSearch } from "../../../shared/components/CorrectionsTextSearch";
import { ProductsList } from "../components/ProductsList";
import { useBbdCorrectionActions } from "../stores/useBbdCorrectionActions";
import { useBbdCorrectionStore } from "../stores/useBbdCorrectionStore";

export function BbdCorrectionIdlePage() {
  const intl = useIntl();
  const navigate = useNavigate();
  const { processBarcode } = useBarcodeScanner();

  const phase = useBbdCorrectionStore((state) => state.phase);
  const searchResults = useBbdCorrectionStore((state) => state.inventoryProductsList);

  const isIdle = phase === "idle";
  const isResolvingProducts = phase === "resolvingProducts";
  const isSearching = phase === "search" || phase === "searchLoading";
  const isSearchLoading = phase === "searchLoading";

  const { scan, activateSearch, cancelSearch, searchProducts, resetSearchResults, selectProduct } =
    useBbdCorrectionActions();

  const onClickProduct = useCallback(
    (product: InventoryProduct) => {
      selectProduct(product.productSku, product.shelf);
    },
    [selectProduct],
  );

  const onSearch = useCallback(
    (query: string) => {
      if (query.length >= 3) {
        searchProducts(query);
      } else {
        resetSearchResults();
      }
    },
    [searchProducts, resetSearchResults],
  );

  const onCancel = useCallback(() => {
    cancelSearch();
  }, [cancelSearch]);

  const onFocusSearchBar = useCallback(() => {
    if (!isSearching) {
      activateSearch();
    }
  }, [isSearching, activateSearch]);

  useScan({
    onScan: (scannedValue) => {
      if (!isIdle) return;
      if (isShelfNumberFormat(scannedValue)) return;

      const barcode = processBarcode(scannedValue);
      if (!barcode) return;

      scan(barcode);
    },
  });

  const searchResultsList = (
    <ProductsList variantSize="s" products={searchResults} onClickProduct={onClickProduct} />
  );

  return (
    <Page isFull isBgGrey h="100%" justifyContent="start">
      {!isSearching && (
        <NavigationHeader
          title={intl.formatMessage({
            id: "components.inventory.bbd-correction.idle.header-title",
          })}
          onClickGoBack={() => navigate(routes.inventory.root)}
        />
      )}

      <CorrectionsTextSearch
        isLoading={isSearchLoading}
        isSearchActive={isSearching}
        searchResults={searchResults}
        onSearch={onSearch}
        onFocusSearchBar={onFocusSearchBar}
        onCancel={onCancel}
        searchResultsList={searchResultsList}
      />

      {!isSearching && (
        <Flex
          flex={1}
          flexDirection="column"
          w="100%"
          overflowY="scroll"
          alignItems="center"
          justifyContent="center"
          p="s200"
        >
          <ScanPlaceholder
            title="pages.inventory.bbd-correction.instructions.title"
            subtitle="pages.inventory.bbd-correction.instructions.subtitle"
          />
        </Flex>
      )}
      <SpinnerModal isOpen={isResolvingProducts} />
    </Page>
  );
}
